import React from "react";

const MeetingAsStageVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson reframes meetings as the stage where your brand is judged in real time. It maps three roles you play: anchor meetings you own, spectator meetings where you listen for leadership themes, and supporting cast meetings where behavior under pressure signals readiness. It stresses having a protected weekly anchor meeting, preparing like a pitch, speaking in the language leaders repeat, and responding to conflict with forward motion.
      </p>
    </div>
  );

  const transcript = (
  <div className="pt-2 pb-6 space-y-5 text-body leading-7 text-[#323232] overflow-y-scroll font-gotham">
    <p>
      Every movie has its stars, the lead, the supporting cast, the audience
      that applauds. But what if I say corporate meetings work that way too.
    </p>

    <p>
      Most people see meetings as time sinks on a calendar.
      <br />
      A necessary evil. Something you “get through” before real work begins.
    </p>

    <p>
      The truth is meetings are not time-blocks. They’re the stage where your
      brand performs. The audience, your leaders, peers, and team, sees who
      you really are when the spotlight hits. And how you show up there…
      decides how far you go.
    </p>

    <p>
      Like every good story, there are <strong>three roles</strong> you’ll play
      on this stage:
      <br />
      <strong>The Anchor</strong> — the lead actor, owner of the meeting
      <br />
      <strong>The Spectator</strong> — silent observer
      <br />
      <strong>The Supporting Cast</strong> — the trusted side character,
      contributing but not driving
    </p>

    <p>
      This is your most powerful meeting. It’s the one you{" "}
      <strong>own, lead, and run.</strong> Your name is on the invite. Your{" "}
      <strong>key stakeholders</strong> are in the room. It{" "}
      <em>CANNOT</em> happen without you.
    </p>

    <p>
      Look at your weekly calendar. Do you have{" "}
      <strong>one meeting every week</strong> that fits this description?
    </p>

    <p>If not, that’s your first action item.</p>

    <p>
      Promotable people protect this meeting like oxygen. Remember these three
      rules:
    </p>

    <ol className="list-decimal list-inside space-y-2 pl-5">
      <li>
        <strong>Protect it.</strong>
        <br />
        When leaders try to skip, don’t delete it. Reschedule.
        <br />
        Make it a habit for them, they should feel its absence.
      </li>

      <li>
        <strong>Prepare for it.</strong>
        <br />
        Don’t wing it. Budget time to prepare like you would for a client
        pitch.
        <br />
        Your brand lives in the first five minutes of how you open that
        meeting.
      </li>

      <li>
        <strong>Perform in it.</strong>
        <br />
        Keep updates sharp and outcome-focused.
        <br />
        Frame problems as choices:{" "}
        “We can do A or B — here’s what each unlocks.”
        <br />
        End every call on an optimistic note.
      </li>
    </ol>

    <p>
      These are the ones you attend but don’t run. Town halls, leadership
      updates, product reviews.
    </p>

    <p>
      For most people, this is background noise. But for the promotables? They
      are gold. While others scroll, they{" "}
      <strong>listen for patterns.</strong>
    </p>

    <p>
      Notice the words leadership repeats, “Efficiency,”
      “Customer,”“Cost discipline,”“AI.” These
      aren’t random; they’re <strong>themes.</strong> They tell you what the
      company values <em>this quarter.</em>
    </p>

    <p>
      Smart professionals don’t just listen to these words, they start
      speaking them. They align their communication, updates, and wins in the
      same language leadership uses. That’s how they stay in tune with what
      matters most.
    </p>

    <p>
      You’re not in charge here, but your presence matters. It’s someone
      else’s anchor meeting, maybe a team member’s or project owner’s and
      you’re in it because your team is part of the story.
    </p>

    <p>
      These meetings are a little tricky. This is where real promotion
      potential shows up. Because how you behave{" "}
      <em>when you’re not the lead</em> tells leaders if you’re ready for the
      next level.
    </p>

    <p>
      When conflict arises, promotables don’t react, they respond. They
      proactively look for solutions without being asked and always push the
      conversation forward, not sideways.
    </p>

    <p>
      But for most people, something else shows up,{" "}
      <strong>their inner saboteur.</strong>
    </p>

    <p>
      Every actor faces one, that quiet voice that forgets the lines when it’s
      time to perform, and it's time to meet some of them.
    </p>

    <p>
      <strong>What's stopping your best performance?</strong>
    </p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default MeetingAsStageVideo;
